import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, Monitor, Settings, Swords, Copy, CheckCircle, Search, Package, FolderSync } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

const REALMLIST = 'set realmlist logon.frostbound.org';

const steps = [
  {
    id: 1,
    icon: Download,
    title: 'Descarga el Cliente',
    desc: 'Descarga el cliente oficial de WoW Wrath of the Lich King versión 3.3.5a (Estándar o HD, arriba).',
    action: null,
    badge: null,
  },
  {
    id: 2,
    icon: Monitor,
    title: 'Instala el Juego',
    desc: 'Descomprime el archivo en tu carpeta preferida. No es necesario un instalador. Compatible con Windows 7/10/11.',
    action: null,
    badge: null,
  },
  {
    id: 3,
    icon: Settings,
    title: 'Configura el Realmlist',
    desc: 'Abre la carpeta del juego, edita Data/esES/realmlist.wtf y reemplaza el contenido con:',
    action: 'realmlist',
    badge: null,
  },
  {
    id: 4,
    icon: Swords,
    title: 'Crea tu Cuenta y Juega',
    desc: 'Regístrate en nuestra web, inicia el juego con Wow.exe y accede con tus credenciales.',
    action: 'registro',
    badge: null,
  },
];

const CLIENT_DOWNLOAD_URL = 'https://gofile.io/d/TmYxV0';

const clientOptions = [
  {
    id: 'standard',
    title: 'Cliente Estándar',
    size: '17.6 GB',
    desc: 'El cliente base de WoW 3.3.5a, ligero y compatible con cualquier equipo.',
    reqs: [
      ['CPU', 'Doble núcleo'],
      ['RAM', '2 GB'],
      ['GPU', 'Compatible con DirectX 9'],
      ['Disco', '~25 GB libres'],
    ],
  },
  {
    id: 'hd',
    title: 'Cliente HD',
    size: '27 GB',
    desc: 'Modelos y texturas en alta resolución, mapas de mazmorras y menú de creación de personaje estilo Retail.',
    reqs: [
      ['CPU', 'Doble núcleo'],
      ['RAM', '4 GB recomendado'],
      ['GPU', 'Compatible con Vulkan (GTX 600+ / GCN+)'],
      ['Disco', '~30 GB libres'],
    ],
  },
];

// Claves = identificadores de categoría del backend (addons_catalog.py).
const CATEGORY_COLORS = {
  interfaz:    { bg: 'rgba(0,229,255,0.1)',   border: 'rgba(0,229,255,0.4)',   text: '#00e5ff' },
  combate:     { bg: 'rgba(251,146,60,0.1)',  border: 'rgba(251,146,60,0.4)',  text: '#fb923c' },
  alertas:     { bg: 'rgba(167,139,250,0.1)', border: 'rgba(167,139,250,0.4)', text: '#a78bfa' },
  pvp:         { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.4)',   text: '#f87171' },
  misiones:    { bg: 'rgba(250,204,21,0.1)',  border: 'rgba(250,204,21,0.4)',  text: '#facc15' },
  inventario:  { bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.4)', text: '#cbd5e1' },
  economia:    { bg: 'rgba(234,179,8,0.1)',   border: 'rgba(234,179,8,0.4)',   text: '#eab308' },
  profesiones: { bg: 'rgba(132,204,22,0.1)',  border: 'rgba(132,204,22,0.4)',  text: '#a3e635' },
  colecciones: { bg: 'rgba(236,72,153,0.1)',  border: 'rgba(236,72,153,0.4)',  text: '#f472b6' },
  social:      { bg: 'rgba(96,165,250,0.1)',  border: 'rgba(96,165,250,0.4)',  text: '#60a5fa' },
  utilidades:  { bg: 'rgba(52,211,153,0.1)',  border: 'rgba(52,211,153,0.4)',  text: '#34d399' },
};

function AddonImage({ src, alt }) {
  const [errored, setErrored] = useState(false);
  if (errored || !src) {
    return (
      <div className="addon-img-fallback">
        <Package size={32} style={{ color: 'rgba(0,229,255,0.4)' }} />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="addon-img"
      onError={() => setErrored(true)}
    />
  );
}

function AddonsContent() {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('TODOS');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/addons`)
      .then(({ data }) => setAddons(data))
      .catch(() => setAddons([]))
      .finally(() => setLoading(false));
  }, []);

  // [id, nombre visible] de las categorías que tienen algún addon.
  const categories = [['TODOS', 'Todos'], ...Array.from(
    new Map(addons.map(a => [a.category, a.category_label || a.category])).entries())];

  const filtered = addons.filter((addon) => {
    const matchesCategory = activeCategory === 'TODOS' || addon.category === activeCategory;
    const q = search.toLowerCase();
    const matchesSearch = !q ||
      addon.name.toLowerCase().includes(q) ||
      (addon.description || '').toLowerCase().includes(q) ||
      (addon.category_label || addon.category || '').toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return <p style={{ color: 'rgba(0,229,255,0.4)', textAlign: 'center', padding: 40 }}>Cargando addons...</p>;
  }

  return (
    <div className="addon-section anim-fade-up" style={{ animationDelay: '0.15s' }}>
      {/* Search + category filter */}
      <div className="addon-controls">
        <div className="addon-search-wrap">
          <Search size={16} className="addon-search-icon" />
          <input
            type="text"
            className="addon-search"
            placeholder="Buscar addon..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="addon-categories">
          {categories.map(([cat, label]) => (
            <button
              key={cat}
              className={`addon-cat-btn ${activeCategory === cat ? 'addon-cat-btn--active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="addon-empty">No se encontraron addons.</p>
      ) : (
        <div className="addon-grid">
          {filtered.map((addon) => {
            const colors = CATEGORY_COLORS[addon.category] || CATEGORY_COLORS.utilidades;
            const dlUrl = addon.download_url;
            return (
              <div
                key={addon._id || addon.name}
                className={`addon-card ${addon.required ? 'addon-card--featured' : ''}`}
              >
                {addon.required && (
                  <div className="addon-required-banner">⚠ RECOMENDADO</div>
                )}

                <div className="addon-img-wrap">
                  <AddonImage
                    src={addon.image_url
                      ? (addon.image_url.startsWith('/') ? `${BACKEND_URL}${addon.image_url}` : addon.image_url)
                      : null}
                    alt={addon.name}
                  />
                </div>

                <div className="addon-body">
                  <div className="addon-header">
                    <span className="addon-name">{addon.name}</span>
                    <span className="addon-badge" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }}>
                      {addon.category_label || addon.category}
                    </span>
                  </div>

                  <p className="addon-desc">{addon.description}</p>

                  {addon.version && (
                    <div className="addon-meta">
                      <span className="addon-meta-item">v{addon.version}</span>
                    </div>
                  )}

                  <a
                    href={dlUrl}
                    className={`addon-dl-btn ${addon.required ? 'addon-dl-btn--featured' : ''}`}
                    download={dlUrl && dlUrl.startsWith('/') ? addon.name : undefined}
                    target={dlUrl && dlUrl.startsWith('/') ? undefined : '_blank'}
                    rel="noopener noreferrer"
                  >
                    <Download size={14} style={{ marginRight: 6 }} />
                    DESCARGAR
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Frostbound Launcher 1.0.0 (portable, sin auto-actualización). Lee
 * /api/launcher/download y no pinta nada mientras `enabled` sea false: se activa
 * desde Mongo (launcher_download.enabled), sin redeploy.
 */
function LauncherDownloadCta() {
  const [launcher, setLauncher] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/launcher/download`)
      .then(({ data }) => setLauncher(data?.enabled ? data : null))
      .catch(() => setLauncher(null));
  }, []);

  if (!launcher) return null;

  const sizeMb = launcher.size ? (launcher.size / (1024 * 1024)).toFixed(1) : null;
  const copySha = () => {
    navigator.clipboard?.writeText(launcher.sha256).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };
  const hint = { color: 'rgba(255,255,255,0.45)', fontSize: '11.5px', marginTop: '6px', lineHeight: 1.5 };

  return (
    <div className="dl-cta anim-fade-up" style={{ animationDelay: '0.2s' }}>
      <a
        href={launcher.url}
        download={launcher.file_name}
        className="dl-main-download-btn"
        data-testid="download-launcher-btn"
      >
        <Download size={30} className="dl-main-download-btn-icon" />
        <div className="dl-main-download-btn-text">
          <span className="dl-main-download-btn-title">
            DESCARGAR FROSTBOUND LAUNCHER
            <span className="dl-launcher-badge">NUEVO</span>
          </span>
          <span className="dl-main-download-btn-sub">
            v{launcher.version} · Windows 10/11 · .zip{sizeMb ? ` · ${sizeMb} MB` : ''}
          </span>
        </div>
      </a>
      <p style={{ ...hint, wordBreak: 'break-all' }}>
        SHA-256: <code style={{ color: 'rgba(255,255,255,0.6)' }}>{launcher.sha256}</code>{' '}
        <button
          type="button"
          onClick={copySha}
          style={{ color: '#00e5ff', background: 'none', border: 0, cursor: 'pointer', fontSize: '11px', textDecoration: 'underline' }}
        >
          {copied ? 'copiado' : 'copiar'}
        </button>
      </p>
    </div>
  );
}

function ClienteContent({ copied, onCopy }) {
  return (
    <>
      {/* Launcher 1.0.0: solo aparece con launcher_download.enabled = true en Mongo
          (desactivado desde 2026-09-21 por un falso positivo del antivirus). */}
      <LauncherDownloadCta />

      {/* Client download options */}
      <div className="dl-client-options anim-fade-up" style={{ animationDelay: '0.2s' }}>
        {clientOptions.map((opt) => (
          <div className="dl-client-card" key={opt.id}>
            <div className="dl-client-card-head">
              <span className="dl-client-card-title">{opt.title}</span>
              <span className="dl-client-card-size">{opt.size}</span>
            </div>
            <p className="dl-client-card-desc">{opt.desc}</p>
            <div className="dl-client-card-reqs">
              {opt.reqs.map(([label, value]) => (
                <span key={label}><b>{label}:</b> {value}</span>
              ))}
            </div>
            <a
              href={CLIENT_DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="dl-client-card-btn"
              data-testid={`download-client-${opt.id}`}
            >
              <Download size={14} />
              DESCARGAR
            </a>
          </div>
        ))}
      </div>

      {/* Tip: switching between Standard/HD without losing settings */}
      <div className="dl-tip anim-fade-up" style={{ animationDelay: '0.22s' }}>
        <FolderSync size={20} className="dl-tip-icon" />
        <div className="dl-tip-body">
          <span className="dl-tip-title">¿Vas a cambiar de Estándar a HD (o viceversa)?</span>
          <p className="dl-tip-text">
            Ambos son la misma versión 3.3.5a por debajo, así que puedes conservar tu
            configuración: cierra WoW y copia la carpeta <code>WTF</code> del cliente
            antiguo al nuevo (sobrescribiendo). Ahí se guardan los ajustes de tus
            addons, macros, keybinds e interfaz por cuenta y personaje.
          </p>
        </div>
      </div>

      {/* Steps divider */}
      <div className="dl-steps-divider anim-fade-up" style={{ animationDelay: '0.25s' }}>
        <div className="dl-steps-divider-line" />
        <div className="dl-steps-divider-text">
          <span className="dl-steps-divider-diamond" />
          SOLO 4 PASOS PARA JUGAR EN FROSTBOUND
          <span className="dl-steps-divider-diamond" />
        </div>
        <div className="dl-steps-divider-line dl-steps-divider-line--right" />
      </div>

      {/* Steps */}
      <div className="dl-steps anim-fade-up" style={{ animationDelay: '0.3s' }} data-testid="dl-steps">
        {steps.map((step) => {
          const Icon = step.icon;
          return (
            <div key={step.id} className="dl-step-card" data-testid={`dl-step-${step.id}`}>
              <div className="dl-step-num">{step.id}</div>
              <div className="dl-step-icon">
                <Icon size={22} />
              </div>
              <div className="dl-step-content">
                <h3 className="dl-step-title">{step.title}</h3>
                <p className="dl-step-desc">{step.desc}</p>
                {step.action === 'realmlist' && (
                  <div className="dl-realmlist" data-testid="realmlist-box">
                    <code className="realmlist-code">{REALMLIST}</code>
                    <button
                      className="copy-btn"
                      onClick={onCopy}
                      data-testid="copy-realmlist"
                    >
                      {copied ? (
                        <><CheckCircle size={13} /> Copiado</>
                      ) : (
                        <><Copy size={13} /> Copiar</>
                      )}
                    </button>
                  </div>
                )}
                {step.action === 'registro' && (
                  <Link to="/registro" className="dl-registro-btn" data-testid="registro-btn">
                    <Swords size={14} style={{ marginRight: 6 }} />
                    CREAR CUENTA
                  </Link>
                )}
                {step.badge && (
                  <span className="dl-badge">{step.badge}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default function DescargasPage() {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('cliente');

  const copyRealmlist = () => {
    navigator.clipboard.writeText(REALMLIST);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <PageWrapper scrollable>
      <SEOMeta
        title="Descargar WoW 3.3.5a | Cliente y addons"
        description="Descarga el cliente WoW 3.3.5a, configura el realmlist de Frostbound e instala los addons recomendados para conectarte al servidor."
        canonical="/descargas"
      />
      <Navbar />

      <main className="dl-main">
        {/* Header */}
        <div className="dl-header anim-fade-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="shop-title" data-testid="descargas-heading">
            <Download size={30} className="shop-title-icon" />
            DESCARGAS
          </h1>
          <p className="shop-subtitle">
            Todo lo que necesitas para unirte a{' '}
            <span className="cyan-highlight">Frostbound.</span>
          </p>
          <p className="dl-file-info" style={{ marginTop: 6 }}>
            Frostbound es un servidor con sistema de Warband: puedes jugar con varios
            personajes propios como grupo, en solitario o con amigos.{' '}
            <Link to="/jugabilidad" className="terms-link">Más sobre esto</Link>.
          </p>
        </div>

        {/* Tabs */}
        <div className="dl-tabs anim-fade-up" style={{ animationDelay: '0.15s' }}>
          <button
            className={`dl-tab ${activeTab === 'cliente' ? 'dl-tab--active' : ''}`}
            onClick={() => setActiveTab('cliente')}
            data-testid="tab-cliente"
          >
            <Download size={14} style={{ marginRight: 6 }} />
            CLIENTE
          </button>
          <button
            className={`dl-tab ${activeTab === 'addons' ? 'dl-tab--active' : ''}`}
            onClick={() => setActiveTab('addons')}
            data-testid="tab-addons"
          >
            <Package size={14} style={{ marginRight: 6 }} />
            ADDONS
          </button>
        </div>

        {activeTab === 'cliente' ? (
          <ClienteContent copied={copied} onCopy={copyRealmlist} />
        ) : (
          <AddonsContent />
        )}
      </main>

      <Footer />
    </PageWrapper>
  );
}
